/**
 * Instructor AI Profiles — list + edit page.
 * Route: /kcis/admin/instructor-profiles
 *
 * Each profile is a platform-wide identity: name + credentials + photo,
 * ElevenLabs voice config, pronunciation dictionary, and teaching
 * philosophy. Any AI generation across KCIS (KAPE, podcasts, Check In,
 * future coaching agents) reads from this collection.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

export default function InstructorProfiles() {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const archivedCount = useMemo(
    () => (items || []).filter((p) => p.active === false).length,
    [items]);
  const visibleItems = useMemo(
    () => (items || []).filter(
      (p) => showArchived ? true : p.active !== false),
    [items, showArchived]);

  const reload = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/kcis/admin/instructor-profiles");
      setItems(data.items || []);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/kcis/admin/instructor-profiles", {
        identity: { full_name: "New Instructor", faculty_title: "Faculty" },
      });
      toast.success("Instructor profile created");
      await reload();
      setOpenId(data.profile.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create profile");
    } finally { setCreating(false); }
  };

  return (
    <KcisAdminShell>
      <div className="max-w-6xl mx-auto px-8 py-10" data-testid="instructor-profiles-page">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KCIS · University · Faculty
            </div>
            <h1 className="text-3xl font-semibold mt-1">Instructor AI Profiles</h1>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: PAL.muted }}>
              Platform-wide identity for every AI experience — KAPE courses,
              podcasts, Check In with Dr. C, marketing, and future coaching
              agents. Define voice, pronunciation, and teaching philosophy once.
            </p>
            <label className="mt-3 inline-flex items-center gap-2 text-xs"
                    style={{ color: PAL.muted }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="ip-show-archived-toggle"
              />
              Show archived profiles
              {archivedCount > 0 && (
                <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: "#0F172A", color: "#E2E8F0" }}
                      data-testid="ip-archived-count">
                  {archivedCount} archived
                </span>
              )}
            </label>
          </div>
          <div className="flex items-center gap-2">
            <BulkRemoveTestProfiles onDone={reload} />
            <button onClick={create} disabled={creating}
                    className="rounded-full px-4 py-2 text-sm font-medium"
                    style={{ background: PAL.accent, color: "#0F0B14" }}
                    data-testid="instructor-profile-new">
              {creating ? "Creating…" : "+ New profile"}
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {items === null && <div className="text-sm" style={{ color: PAL.muted }}>Loading…</div>}
          {items && visibleItems.length === 0 && (
            <div className="rounded-xl p-6 border" style={{ borderColor: PAL.border, background: PAL.panel }}
                  data-testid="ip-empty-state">
              <div className="text-sm" style={{ color: PAL.muted }}>
                {items.length === 0
                  ? "No instructor profiles yet."
                  : "No active instructor profiles. Toggle \"Show archived "
                    + "profiles\" to view archived ones."}
              </div>
            </div>
          )}
          {items && visibleItems.map((p) => (
            <ProfileRow key={p.id} profile={p} open={openId === p.id}
                        onToggle={() => setOpenId(openId === p.id ? null : p.id)}
                        onSaved={reload} />
          ))}
        </div>
      </div>
    </KcisAdminShell>
  );
}


function ProfileRow({ profile, open, onToggle, onSaved }) {
  return (
    <div className="rounded-2xl border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`instructor-profile-row-${profile.slug}`}>
      <button onClick={onToggle}
              className="w-full flex items-center gap-4 px-5 py-4 text-left"
              data-testid={`instructor-profile-row-${profile.slug}-toggle`}>
        <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center"
             style={{ background: PAL.accentSoft }}>
          {profile.identity.photo_url ? (
            <img src={profile.identity.photo_url} alt=""
                 className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg font-semibold" style={{ color: PAL.accent }}>
              {(profile.identity.full_name || "?").slice(0, 1)}
            </span>
          )}
        </div>
        <div className="flex-1">
          <div className="font-semibold" style={{ color: PAL.text }}>
            {profile.identity.full_name}
            {profile.is_default && (
              <span className="ml-2 text-[10px] uppercase px-2 py-0.5 rounded"
                    style={{ background: PAL.accent, color: "#0F0B14" }}>
                Default
              </span>
            )}
            {profile.active === false && (
              <span className="ml-2 text-[10px] uppercase px-2 py-0.5 rounded"
                    style={{ background: "#94A3B8", color: "#0F172A" }}
                    data-testid={`ip-${profile.slug}-archived-badge`}>
                Archived
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5" style={{ color: PAL.muted }}>
            {profile.identity.faculty_title} · {profile.identity.credentials}
          </div>
          <div className="text-xs mt-1" style={{ color: PAL.muted }}>
            Voice: {profile.voice.voice_name || "—"} · Pronunciation: {profile.pronunciation.length} entries · Phrases: {profile.philosophy.signature_phrases.length}
          </div>
        </div>
        <span className="text-sm" style={{ color: PAL.accent }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <ProfileEditor profile={profile} onSaved={onSaved} />}
    </div>
  );
}


function ProfileEditor({ profile, onSaved }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(profile)));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const patch = (path, value) => {
    const next = { ...form };
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = { ...cur[parts[i]] };
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    setForm(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/kcis/admin/instructor-profiles/${profile.id}`, {
        identity: form.identity,
        voice: form.voice,
        pronunciation: form.pronunciation,
        philosophy: form.philosophy,
        tags: form.tags,
        active: form.active,
        is_default: form.is_default,
      });
      toast.success("Saved");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/kcis/admin/instructor-profiles/${profile.id}/photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      patch("identity.photo_url", data.photo_url);
      toast.success("Photo uploaded");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const [removing, setRemoving] = useState(false);
  const remove = async () => {
    if (removing) return;   // idempotent-click guard
    const name = form.identity?.full_name || "this instructor";
    const msg = `Remove ${name}? This profile will no longer be `
                + `available for new course assignments.`;
    if (typeof window !== "undefined" && !window.confirm(msg)) return;
    setRemoving(true);
    try {
      await api.delete(`/kcis/admin/instructor-profiles/${profile.id}`);
      // Confirm the SERVER state — do not trust the HTTP 200 alone.
      // The list endpoint returns the (now-archived) row with
      // active:false; verify that before telling the owner "removed".
      const { data } = await api.get("/kcis/admin/instructor-profiles");
      const stillActive = (data.items || []).find(
        (p) => p.id === profile.id && p.active !== false);
      if (stillActive) {
        toast.error("Removal did not take effect. Please retry.");
      } else {
        toast.success(`${name} removed.`);
      }
      await onSaved();     // pulls fresh list into the parent
    } catch (e) {
      const d = e?.response?.data?.detail;
      const message = typeof d === "string" ? d
        : d?.message || "Could not remove profile.";
      toast.error(message);
    } finally { setRemoving(false); }
  };

  const addPronunciation = () => {
    setForm({ ...form, pronunciation: [...(form.pronunciation || []), { term: "", phonetic: "", note: "" }] });
  };
  const removePronunciation = (i) => {
    setForm({ ...form, pronunciation: form.pronunciation.filter((_, idx) => idx !== i) });
  };

  const addPhrase = () => {
    patch("philosophy.signature_phrases", [...(form.philosophy.signature_phrases || []), ""]);
  };

  return (
    <div className="border-t px-5 py-5 space-y-6" style={{ borderColor: PAL.border }}
         data-testid={`instructor-profile-editor-${profile.slug}`}>
      {/* Identity */}
      <Card title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full name">
            <input value={form.identity.full_name || ""} onChange={(e) => patch("identity.full_name", e.target.value)}
                   className={inputCls} data-testid={`ip-${profile.slug}-full-name`} />
          </Field>
          <Field label="Display name">
            <input value={form.identity.display_name || ""} onChange={(e) => patch("identity.display_name", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Credentials">
            <input value={form.identity.credentials || ""} onChange={(e) => patch("identity.credentials", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Faculty title">
            <input value={form.identity.faculty_title || ""} onChange={(e) => patch("identity.faculty_title", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Biography" col2>
            <textarea rows={3} value={form.identity.biography || ""} onChange={(e) => patch("identity.biography", e.target.value)}
                      className={inputCls} />
          </Field>
          <Field label="Photo" col2>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden flex items-center justify-center"
                   style={{ background: PAL.accentSoft }}>
                {form.identity.photo_url ? (
                  <img src={form.identity.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs" style={{ color: PAL.muted }}>No photo</span>
                )}
              </div>
              <label className={`rounded-full px-3 py-1 text-xs cursor-pointer ${uploading ? "opacity-50" : ""}`}
                     style={{ background: PAL.accent, color: "#0F0B14" }}
                     data-testid={`ip-${profile.slug}-photo-upload`}>
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
            </div>
          </Field>
        </div>
      </Card>

      {/* Voice */}
      <Card title="Voice">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="ElevenLabs Voice ID">
            <input value={form.voice.voice_id || ""} onChange={(e) => patch("voice.voice_id", e.target.value)}
                   placeholder="(uses ELEVENLABS_VOICE_ID when empty)"
                   className={inputCls} data-testid={`ip-${profile.slug}-voice-id`} />
          </Field>
          <Field label="Voice name">
            <input value={form.voice.voice_name || ""} onChange={(e) => patch("voice.voice_name", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Speech rate (0.7–1.2)">
            <input type="number" step="0.05" value={form.voice.speech_rate ?? 0.95}
                   onChange={(e) => patch("voice.speech_rate", parseFloat(e.target.value))}
                   className={inputCls} />
          </Field>
          <Field label="Stability (0–1)">
            <input type="number" step="0.05" value={form.voice.stability ?? 0.65}
                   onChange={(e) => patch("voice.stability", parseFloat(e.target.value))}
                   className={inputCls} />
          </Field>
          <Field label="Similarity (0–1)">
            <input type="number" step="0.05" value={form.voice.similarity_boost ?? 0.80}
                   onChange={(e) => patch("voice.similarity_boost", parseFloat(e.target.value))}
                   className={inputCls} />
          </Field>
          <Field label="Style (0–1)">
            <input type="number" step="0.05" value={form.voice.style ?? 0.12}
                   onChange={(e) => patch("voice.style", parseFloat(e.target.value))}
                   className={inputCls} />
          </Field>
          <Field label="Preferred greeting" col2>
            <input value={form.voice.preferred_greeting || ""} onChange={(e) => patch("voice.preferred_greeting", e.target.value)}
                   className={inputCls} />
          </Field>
        </div>
      </Card>

      {/* Pronunciation */}
      <Card title={`Pronunciation dictionary (${form.pronunciation?.length || 0})`}
             action={<button onClick={addPronunciation} className="text-xs" style={{ color: PAL.accent }}
                              data-testid={`ip-${profile.slug}-pron-add`}>+ Add term</button>}>
        <div className="space-y-2">
          {(form.pronunciation || []).map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input value={p.term || ""} onChange={(e) => {
                const next = [...form.pronunciation]; next[i] = { ...next[i], term: e.target.value };
                setForm({ ...form, pronunciation: next });
              }} placeholder="Term" className={`${inputCls} col-span-3`} />
              <input value={p.phonetic || ""} onChange={(e) => {
                const next = [...form.pronunciation]; next[i] = { ...next[i], phonetic: e.target.value };
                setForm({ ...form, pronunciation: next });
              }} placeholder="try-KOL-uh-jee" className={`${inputCls} col-span-4`} />
              <input value={p.note || ""} onChange={(e) => {
                const next = [...form.pronunciation]; next[i] = { ...next[i], note: e.target.value };
                setForm({ ...form, pronunciation: next });
              }} placeholder="note (optional)" className={`${inputCls} col-span-4`} />
              <button onClick={() => removePronunciation(i)} className="col-span-1 text-xs"
                      style={{ color: "#F87171" }}>Remove</button>
            </div>
          ))}
        </div>
      </Card>

      {/* Philosophy */}
      <Card title="Teaching philosophy">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Teaching style">
            <input value={form.philosophy.teaching_style || ""} onChange={(e) => patch("philosophy.teaching_style", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Tone">
            <input value={form.philosophy.tone || ""} onChange={(e) => patch("philosophy.tone", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Reading level">
            <input value={form.philosophy.reading_level || ""} onChange={(e) => patch("philosophy.reading_level", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Energy">
            <input value={form.philosophy.energy || ""} onChange={(e) => patch("philosophy.energy", e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label={`Signature phrases (${form.philosophy.signature_phrases?.length || 0})`} col2>
            <div className="space-y-2">
              {(form.philosophy.signature_phrases || []).map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input value={s} onChange={(e) => {
                    const next = [...form.philosophy.signature_phrases]; next[i] = e.target.value;
                    patch("philosophy.signature_phrases", next);
                  }} className={`${inputCls} flex-1`} />
                  <button onClick={() => {
                    patch("philosophy.signature_phrases", form.philosophy.signature_phrases.filter((_, idx) => idx !== i));
                  }} className="text-xs" style={{ color: "#F87171" }}>×</button>
                </div>
              ))}
              <button onClick={addPhrase} className="text-xs" style={{ color: PAL.accent }}>+ Add phrase</button>
            </div>
          </Field>
        </div>
      </Card>

      {/* Actions — hidden for archived rows; the Archived badge is
          already visible on the row header and no further mutation
          is appropriate here (a Restore affordance is intentionally
          out of scope for this repair). */}
      {profile.active === false ? (
        <div className="pt-2 text-xs" style={{ color: PAL.muted }}
             data-testid={`ip-${profile.slug}-archived-notice`}>
          This profile is archived and hidden from active lists and
          instructor dropdowns.
          {profile.archived_at && (
            <> Archived {new Date(profile.archived_at).toLocaleString()}
              {profile.archived_by ? ` by ${profile.archived_by}` : ""}.
            </>
          )}
        </div>
      ) : (
      <div className="flex items-center gap-3 pt-2 flex-wrap">
        <button onClick={save} disabled={saving}
                className="rounded-full px-5 py-2 text-sm font-medium"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid={`ip-${profile.slug}-save`}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={remove} disabled={removing || profile.is_default}
                className="rounded-full px-4 py-2 text-sm font-medium"
                style={{ background: (removing || profile.is_default)
                                       ? "#94A3B8" : "#DC2626",
                         color: "#FFFFFF",
                         cursor: (removing || profile.is_default)
                                       ? "not-allowed" : "pointer",
                         opacity: (removing || profile.is_default) ? 0.7 : 1 }}
                title={profile.is_default
                        ? "The default profile cannot be removed."
                        : "Remove this instructor profile (soft archive)."}
                data-testid={`ip-${profile.slug}-remove`}>
          {removing ? "Removing…" : "Remove Profile"}
        </button>
        <label className="flex items-center gap-2 text-xs" style={{ color: PAL.muted }}>
          <input type="checkbox" checked={!!form.is_default}
                 onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                 data-testid={`ip-${profile.slug}-default`} />
          Set as platform default
        </label>
        <label className="flex items-center gap-2 text-xs" style={{ color: PAL.muted }}>
          <input type="checkbox" checked={!!form.active}
                 onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active
        </label>
      </div>
      )}
    </div>
  );
}


const inputCls = "w-full rounded-lg bg-transparent border px-3 py-2 text-sm outline-none focus:ring-1";
function Card({ title, action, children }) {
  return (
    <div className="rounded-xl p-4 border" style={{ borderColor: PAL.border, background: "#0F0B14" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Field({ label, col2, children }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      {children}
    </div>
  );
}

// ═════════════════════════ Bulk Remove Test Profiles ═════════════════════════
// Two-step: (1) dry-run POST to fetch the exact list of eligible /
// skipped profiles, (2) show them + confirm, (3) confirm POST archives
// only the safe set. Protected + course-referenced profiles are always
// skipped, even at confirmation.
function BulkRemoveTestProfiles({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const dryRun = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        "/kcis/admin/instructor-profiles/bulk-remove-test-profiles",
        { confirm: false });
      // Backend already scans only active:true profiles, so an empty
      // to_remove + empty skipped means every "Test Instructor" /
      // "Extended Test" row is already archived. Give the owner the
      // precise message from the spec.
      if ((data.to_remove || []).length === 0
          && (data.skipped || []).length === 0) {
        toast.success(
          "All matching test profiles have already been removed from "
          + "the active list.");
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan profiles.");
    } finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (busy || !preview?.to_remove?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        "/kcis/admin/instructor-profiles/bulk-remove-test-profiles",
        { confirm: true });
      toast.success(`Removed ${data.archived_count} test profile(s).`);
      setPreview(null);
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={dryRun} disabled={busy}
              className="rounded-full px-4 py-2 text-sm font-medium"
              style={{ background: "#FFFFFF", color: "#0F172A",
                       border: "1px solid #CBD5E1",
                       cursor: busy ? "not-allowed" : "pointer",
                       opacity: busy ? 0.7 : 1 }}
              title={"Preview which \"Test Instructor\" and \"Extended "
                       + "Test\" profiles are safe to remove."}
              data-testid="instructor-profile-bulk-remove-test-btn">
        {busy && !preview ? "Scanning…" : "Remove Test Profiles"}
      </button>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
             role="dialog" aria-modal="true"
             data-testid="bulk-remove-test-dialog">
          <div className="max-w-lg w-full rounded-xl bg-white text-slate-900 p-6 shadow-xl">
            <div className="text-lg font-semibold">Remove test instructor profiles?</div>
            <div className="mt-1 text-sm text-slate-600">
              The following profiles will be archived. They will no
              longer appear in dropdowns or the active list.
            </div>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-3 text-sm"
                 data-testid="bulk-remove-test-to-remove">
              {preview.to_remove.length === 0
                ? <div className="text-slate-500">Nothing to remove.</div>
                : preview.to_remove.map((p) => (
                    <div key={p.id} className="py-0.5">• {p.name}</div>
                  ))}
            </div>
            {preview.skipped.length > 0 && (
              <>
                <div className="mt-3 text-xs uppercase tracking-widest font-semibold text-slate-700">
                  Skipped ({preview.skipped.length})
                </div>
                <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                     data-testid="bulk-remove-test-skipped">
                  {preview.skipped.map((p) => (
                    <div key={p.id} className="py-0.5">
                      • {p.name} — {p.reason}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPreview(null)} disabled={busy}
                      className="rounded-full px-4 py-2 text-sm font-medium"
                      style={{ background: "#F1F5F9", color: "#0F172A",
                               border: "1px solid #CBD5E1" }}
                      data-testid="bulk-remove-test-cancel">
                Cancel
              </button>
              <button onClick={confirmRemove}
                      disabled={busy || preview.to_remove.length === 0}
                      className="rounded-full px-4 py-2 text-sm font-medium"
                      style={{ background: (busy || preview.to_remove.length === 0)
                                              ? "#94A3B8" : "#DC2626",
                               color: "#FFFFFF",
                               cursor: (busy || preview.to_remove.length === 0)
                                              ? "not-allowed" : "pointer",
                               opacity: (busy || preview.to_remove.length === 0)
                                              ? 0.7 : 1 }}
                      data-testid="bulk-remove-test-confirm">
                {busy ? "Removing…"
                       : `Remove ${preview.to_remove.length} profile(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

